import type { Majority, MajorityBase, QuorumAtClose, Ratio, RuleSet, TieRule, VotingMode } from '@simulador/engine';
import { RULESET_TEMPLATES, type RuleSetTemplate } from '@simulador/ruleset/templates';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { api, ApiError } from '../lib/api.ts';
import { byVersionDesc } from '../lib/rules.ts';
import {
  addMajority, addMatter, AT_CLOSE_LABEL, droppedLocked, BASE_LABEL, diffRuleSets, fieldName, issuesAt, majorityPath, MODE_LABEL, moveMatter, mattersUsing,
  nextFreeVersion, presetIndex, preview, RATIO_PRESETS, removeMajority, removeMatter, setQuorumType, summarize, TIE_LABEL, updateMajority,
  updateMatter, type Issue, type PreviewInput,
} from '../lib/rules-form.ts';
import { ErrorBox } from './ui.tsx';

type RuleSetSummary = { id: string; version: string; note: string | null; createdAt: string };
/** De dónde parte el borrador: una versión registrada o una plantilla. */
type Source = { kind: 'version'; id: string; version: string; rules: RuleSet } | { kind: 'template'; id: string; name: string; basis: string; rules: RuleSet };

const fromTemplate = (t: RuleSetTemplate): Source => ({ kind: 'template', id: t.id, name: t.name, basis: t.basis, rules: t.ruleSet });
const sourceKey = (s: Source) => `${s.kind}:${s.id}`;

export function RulesEditor() {
  const [versions, setVersions] = useState<RuleSetSummary[] | null>(null);
  const [latest, setLatest] = useState<RuleSet | null>(null);
  const [source, setSource] = useState<Source | null>(null);
  const [draft, setDraft] = useState<RuleSet | null>(null);
  const [note, setNote] = useState('');
  const [issues, setIssues] = useState<Issue[]>([]);
  const [checking, setChecking] = useState(false);
  const [members, setMembers] = useState(30);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState<'form' | 'json'>('form');
  // Cambia al partir de otra versión o plantilla: reinicia el estado interno de los campos.
  const [generation, setGeneration] = useState(0);

  const loadVersion = async (id: string): Promise<Source> => {
    const r = await api<{ id: string; ruleSet: RuleSet }>('GET', `/api/rule-sets/${id}`);
    return { kind: 'version', id: r.id, version: r.ruleSet.version, rules: r.ruleSet };
  };

  const start = (s: Source, list: RuleSetSummary[]) => {
    setSource(s);
    setDraft({ ...structuredClone(s.rules), version: nextFreeVersion(list.map((v) => v.version)) });
    setNote('');
    setIssues([]);
    setView('form');
    setGeneration((g) => g + 1);
  };

  const load = async (startFrom?: string) => {
    try {
      const list = byVersionDesc((await api<{ ruleSets: RuleSetSummary[] }>('GET', '/api/rule-sets')).ruleSets);
      setVersions(list);
      const newest = list[0] ? await loadVersion(list[0].id) : null;
      setLatest(newest?.rules ?? null);
      const from = startFrom && startFrom !== list[0]?.id ? await loadVersion(startFrom) : newest;
      if (from) start(from, list);
      else start(fromTemplate(RULESET_TEMPLATES[0]!), list);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  useEffect(() => {
    void load();
    api<{ members: Array<{ roles: string[]; active: boolean }> }>('GET', '/api/members')
      .then((r) => setMembers(r.members.filter((m) => m.active && m.roles.includes('legislador')).length || 30))
      .catch(() => {});
  }, []);

  // Ids de tipos de asunto que ya existen en versiones registradas: no cambian, porque los asuntos los guardan.
  const locked = useMemo(() => new Set([...(latest?.matters ?? []), ...(source?.kind === 'version' ? source.rules.matters : [])].map((m) => m.id)), [latest, source]);
  // Contra qué se comparan los cambios: la versión de partida o, si es plantilla, la vigente más nueva.
  const compareTo = source?.kind === 'version' ? source.rules : latest;
  const changes = useMemo(() => (draft && compareTo ? diffRuleSets(compareTo, draft) : []), [draft, compareTo]);
  const dirty = !!(draft && source && diffRuleSets(source.rules, draft).length);
  const dropped = draft ? droppedLocked(draft, locked) : [];
  const labelOf = (id: string) => [...(latest?.matters ?? []), ...(source?.rules.matters ?? [])].find((m) => m.id === id)?.label ?? id;

  // Revisión del servidor mientras se edita (el validador completo no corre en el navegador).
  const seq = useRef(0);
  useEffect(() => {
    if (!draft) return;
    const n = ++seq.current;
    setChecking(true);
    const timer = setTimeout(() => {
      api<{ ok: boolean; errors: Issue[] }>('POST', '/api/rule-sets/validate', { ruleSet: draft })
        .then((r) => { if (n === seq.current) setIssues(r.errors); })
        .catch(() => {})
        .finally(() => { if (n === seq.current) setChecking(false); });
    }, 400);
    return () => clearTimeout(timer);
  }, [draft]);

  const choose = async (key: string) => {
    if (!versions) return;
    if (dirty && !confirm('Tienes cambios sin registrar. ¿Descartarlos?')) return;
    setNotice(null);
    setError(null);
    const [kind, id] = key.split(':') as ['version' | 'template', string];
    try {
      if (kind === 'version') start(await loadVersion(id), versions);
      else start(fromTemplate(RULESET_TEMPLATES.find((t) => t.id === id)!), versions);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await api<{ id: string; version: string }>('POST', '/api/rule-sets', { ruleSet: draft, ...(note.trim() ? { note } : {}) });
      setNotice(`Versión ${res.version} registrada. Para usarla, elígela en la sesión (pestaña Sesiones).`);
      await load(res.id);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'version_exists' && versions) {
        // Alguien registró otra versión mientras tanto: se toma la siguiente libre.
        const list = byVersionDesc((await api<{ ruleSets: RuleSetSummary[] }>('GET', '/api/rule-sets')).ruleSets);
        setVersions(list);
        setDraft({ ...draft, version: nextFreeVersion(list.map((v) => v.version)) });
        setError('Otra persona registró una versión mientras editabas. Revisa los cambios y vuelve a registrar.');
      } else {
        setError((e as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  if (!draft || !source || !versions) return <div class="panel"><ErrorBox error={error} />{!error && <p class="hint">Cargando reglas…</p>}</div>;

  const edit = (next: RuleSet) => { setNotice(null); setDraft(next); };
  const fieldIssues = (prefix: string) => <FieldIssues issues={issuesAt(issues, prefix)} />;

  return (
    <div class="rules-editor">
      <section class="panel">
        <div class="field">
          <label for="partir">Partir de</label>
          <select id="partir" value={sourceKey(source)} onChange={(e) => void choose(e.currentTarget.value)}>
            <optgroup label="Versiones registradas">
              {versions.map((v, i) => <option key={v.id} value={`version:${v.id}`}>{v.version}{i === 0 ? ' (la más reciente)' : ''}{v.note ? ` · ${v.note}` : ''}</option>)}
            </optgroup>
            <optgroup label="Plantillas">
              {RULESET_TEMPLATES.map((t) => <option key={t.id} value={`template:${t.id}`}>{t.name}</option>)}
            </optgroup>
          </select>
          {source.kind === 'template' && <p class="hint">{source.basis} Revísala contra el reglamento de tu organización.</p>}
        </div>
        <p class="hint">Las reglas no se editan: cada cambio se registra como una versión nueva (esta será <b>{draft.version}</b>). Las votaciones ya abiertas o cerradas conservan la versión con la que se abrieron.</p>
        <div class="row" role="tablist" aria-label="Forma de editar">
          <button class={`btn ${view === 'form' ? 'primary' : ''}`} role="tab" aria-selected={view === 'form'} onClick={() => setView('form')}>Formulario</button>
          <button class={`btn ${view === 'json' ? 'primary' : ''}`} role="tab" aria-selected={view === 'json'} onClick={() => setView('json')}>Avanzado (JSON)</button>
        </div>
      </section>

      <div class="rules-main">
        <div class="rules-form" key={generation}>
          {view === 'json'
            ? <JsonEditor draft={draft} onApply={(r) => { edit({ ...r, version: draft.version }); setView('form'); }} />
            : (
              <>
                <section class="panel">
                  <h2>General</h2>
                  <div class="field">
                    <label for="empate">Si hay empate</label>
                    <select id="empate" value={draft.tie} onChange={(e) => edit({ ...draft, tie: e.currentTarget.value as TieRule })}>
                      {Object.entries(TIE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                    <p class="hint">El empate aplica a mayorías de la mitad sobre votos o presentes; sobre el total de integrantes no hay empate posible.</p>
                  </div>
                  <div class="field">
                    <label for="modalidad">Modalidad de voto</label>
                    <select id="modalidad" value={draft.mode} onChange={(e) => edit({ ...draft, mode: e.currentTarget.value as VotingMode })}>
                      {Object.entries(MODE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                    <p class="hint">Cada tipo de asunto puede tener la suya (por ejemplo, elecciones en secreto).</p>
                  </div>
                  {fieldIssues('/tie')}{fieldIssues('/mode')}
                </section>

                <QuorumSection draft={draft} members={members} onChange={edit} issues={issuesAt(issues, '/quorum')} />

                <section class="panel">
                  <h2>Mayorías</h2>
                  {Object.entries(draft.majorities).map(([key, m]) => (
                    <MajorityCard key={key} id={key} majority={m} usedBy={mattersUsing(draft, key).map((x) => x.label)} only={Object.keys(draft.majorities).length === 1}
                      issues={issuesAt(issues, majorityPath(key))}
                      onChange={(change) => edit(updateMajority(draft, key, change))}
                      onRemove={() => edit(removeMajority(draft, key))} />
                  ))}
                  <button class="btn" onClick={() => edit(addMajority(draft).rules)}>Agregar mayoría</button>
                </section>

                <section class="panel">
                  <h2>Tipos de asunto</h2>
                  <p class="hint">Al agregar un asunto a una sesión se elige uno de estos tipos; el tipo decide la mayoría y, si quieres, la modalidad.</p>
                  {draft.matters.map((m, i) => (
                    <div class="matter-row" key={`${i}-${locked.has(m.id) ? m.id : 'nuevo'}`}>
                      <div class="field">
                        <label for={`tipo-${i}`}>Nombre</label>
                        <input id={`tipo-${i}`} type="text" value={m.label} maxLength={120}
                          onInput={(e) => edit(updateMatter(draft, i, { label: e.currentTarget.value }, locked))} />
                        <span class="num" title={locked.has(m.id) ? 'Id fijo: ya existe en una versión registrada' : 'Id generado a partir del nombre'}>
                          {locked.has(m.id) ? 'id fijo: ' : 'id: '}{m.id}
                        </span>
                      </div>
                      <div class="field">
                        <label for={`tipo-mayoria-${i}`}>Mayoría</label>
                        <select id={`tipo-mayoria-${i}`} value={m.majority} onChange={(e) => edit(updateMatter(draft, i, { majority: e.currentTarget.value }, locked))}>
                          {Object.entries(draft.majorities).map(([k, maj]) => <option key={k} value={k}>{maj.label}</option>)}
                        </select>
                      </div>
                      <div class="field">
                        <label for={`tipo-modo-${i}`}>Modalidad</label>
                        <select id={`tipo-modo-${i}`} value={m.mode ?? ''} onChange={(e) => edit(updateMatter(draft, i, { mode: (e.currentTarget.value || null) as VotingMode | null }, locked))}>
                          <option value="">General ({draft.mode})</option>
                          <option value="nominal">Nominal</option>
                          <option value="secreta">Secreta</option>
                        </select>
                      </div>
                      <div class="row matter-actions">
                        <button class="btn" aria-label={`Subir ${m.label}`} disabled={i === 0} onClick={() => edit(moveMatter(draft, i, -1))}>↑</button>
                        <button class="btn" aria-label={`Bajar ${m.label}`} disabled={i === draft.matters.length - 1} onClick={() => edit(moveMatter(draft, i, 1))}>↓</button>
                        <button class="btn" aria-label={`Quitar ${m.label}`} disabled={draft.matters.length === 1} onClick={() => {
                          if (locked.has(m.id) && !confirm(`Si quitas «${m.label}», los asuntos de ese tipo no se podrán votar con esta versión. ¿Quitarlo?`)) return;
                          edit(removeMatter(draft, i));
                        }}>Quitar</button>
                      </div>
                      <div class="matter-issues">{fieldIssues(`/matters/${i}`)}</div>
                    </div>
                  ))}
                  <button class="btn" onClick={() => edit(addMatter(draft))}>Agregar tipo de asunto</button>
                </section>
              </>
            )}
        </div>

        <aside class="rules-side">
          <section class="panel">
            <h2>Resumen</h2>
            <Summary rules={draft} />
          </section>
          <PreviewPanel rules={draft} members={members} />
          <section class="panel">
            <h2>Cambios contra {source.kind === 'version' ? source.version : latest ? latest.version : 'nada'}</h2>
            {changes.length ? <ul class="changes">{changes.map((c) => <li key={c}>{c}</li>)}</ul> : <p class="hint">Sin cambios todavía.</p>}
            {dropped.length > 0 && (
              <p class="warning" role="note">
                Esta versión ya no tiene {dropped.length === 1 ? 'el tipo de asunto' : 'los tipos de asunto'} {dropped.map((id) => `«${labelOf(id)}»`).join(', ')}.
                Los asuntos de {dropped.length === 1 ? 'ese tipo' : 'esos tipos'} que ya existen en sesiones no se podrán votar si la sesión usa esta versión.
              </p>
            )}
          </section>
          <section class="panel">
            <h2>Registrar</h2>
            <div class="field">
              <label for="nota">Nota del cambio (opcional)</label>
              <input id="nota" type="text" value={note} maxLength={280} placeholder="Sube la mayoría de reformas a dos tercios" onInput={(e) => setNote(e.currentTarget.value)} />
              <p class="hint">Queda en el historial y en la auditoría.</p>
            </div>
            <ErrorBox error={error} />
            {notice && <p class="notice" role="status">{notice}</p>}
            {issues.length > 0 && (
              <div class="error" role="alert">
                <p>Corrige antes de registrar:</p>
                <ul>{issues.map((i) => <li key={i.path + i.message}>{fieldName(i.path, draft)}: {i.message}</li>)}</ul>
              </div>
            )}
            <div class="row">
              <button class="btn primary" disabled={busy || checking || issues.length > 0 || !changes.length && source.kind === 'version'} onClick={save}>
                {busy ? 'Registrando…' : checking ? 'Revisando…' : `Registrar ${draft.version}`}
              </button>
              <button class="btn" disabled={!dirty || busy} onClick={() => start(source, versions)}>Descartar cambios</button>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}

function FieldIssues({ issues }: { issues: Issue[] }) {
  if (!issues.length) return null;
  return <ul class="field-issues" role="alert">{issues.map((i) => <li key={i.path + i.message}>{i.message}</li>)}</ul>;
}

function Comparison({ id, strict, onChange, allowStrict = true }: { id: string; strict: boolean; onChange: (strict: boolean) => void; allowStrict?: boolean }) {
  return (
    <select id={id} aria-label="Comparación" value={strict ? 'mas' : 'al-menos'} onChange={(e) => onChange(e.currentTarget.value === 'mas')}>
      <option value="mas" disabled={!allowStrict}>más de</option>
      <option value="al-menos">al menos</option>
    </select>
  );
}

/** Fracción con atajos (la mitad, dos tercios…) y "otra" para escribir numerador y denominador. */
function RatioInput({ id, value, onChange, minNum }: { id: string; value: Ratio; onChange: (r: Ratio) => void; minNum: number }) {
  const preset = presetIndex(value);
  const [custom, setCustom] = useState(preset < 0);
  const isCustom = custom || preset < 0;
  const term = (n: string, min: number) => Math.min(1000, Math.max(min, Math.trunc(Number(n)) || min));
  return (
    <>
      <select id={id} aria-label="Fracción" value={isCustom ? 'otra' : String(preset)} onChange={(e) => {
        const v = e.currentTarget.value;
        if (v === 'otra') setCustom(true);
        else { setCustom(false); onChange(RATIO_PRESETS[Number(v)]!.value); }
      }}>
        {RATIO_PRESETS.map((p, i) => <option key={p.label} value={String(i)}>{p.label}</option>)}
        <option value="otra">otra fracción…</option>
      </select>
      {isCustom && (
        <span class="ratio">
          <input type="number" inputMode="numeric" aria-label="Numerador" min={minNum} max={1000} value={value.num} onInput={(e) => onChange({ ...value, num: term(e.currentTarget.value, minNum) })} />
          <span aria-hidden="true">/</span>
          <input type="number" inputMode="numeric" aria-label="Denominador" min={1} max={1000} value={value.den} onInput={(e) => onChange({ ...value, den: term(e.currentTarget.value, 1) })} />
        </span>
      )}
    </>
  );
}

function QuorumSection({ draft, members, onChange, issues }: { draft: RuleSet; members: number; onChange: (r: RuleSet) => void; issues: Issue[] }) {
  const q = draft.quorum;
  const set = (quorum: RuleSet['quorum']) => onChange({ ...draft, quorum });
  return (
    <section class="panel">
      <h2>Quórum</h2>
      <div class="field">
        <label for="quorum-tipo">Cómo se cuenta</label>
        <select id="quorum-tipo" value={q.type} onChange={(e) => set(setQuorumType(q, e.currentTarget.value as 'percent' | 'fixed', members))}>
          <option value="percent">Proporción de los integrantes</option>
          <option value="fixed">Número fijo de presentes</option>
        </select>
      </div>
      {q.type === 'percent' ? (
        <div class="field">
          <label for="quorum-fraccion">Se necesita</label>
          <div class="inline">
            <Comparison id="quorum-comparacion" strict={q.strict} allowStrict={q.value.num !== q.value.den} onChange={(strict) => set({ ...q, strict })} />
            <RatioInput id="quorum-fraccion" value={q.value} minNum={0} onChange={(value) => set({ ...q, value, strict: q.strict && value.num !== value.den })} />
            <span>de los integrantes</span>
          </div>
        </div>
      ) : (
        <div class="field">
          <label for="quorum-fijo">Presentes necesarios</label>
          <input id="quorum-fijo" type="number" inputMode="numeric" min={0} max={100000} value={q.value}
            onInput={(e) => set({ ...q, value: Math.max(0, Math.trunc(Number(e.currentTarget.value)) || 0) })} />
        </div>
      )}
      <div class="field">
        <label for="quorum-cierre">Al cerrar la votación</label>
        <select id="quorum-cierre" value={q.atClose ?? 'none'} onChange={(e) => set({ ...q, atClose: e.currentTarget.value as QuorumAtClose })}>
          {Object.entries(AT_CLOSE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <p class="hint">El quórum siempre se exige para abrir. Si además se revisa al cerrar y falta, la votación queda sin efecto y el asunto se puede volver a votar.</p>
      </div>
      <FieldIssues issues={issues} />
    </section>
  );
}

function MajorityCard({ id, majority: m, usedBy, only, issues, onChange, onRemove }: {
  id: string; majority: Majority; usedBy: string[]; only: boolean; issues: Issue[];
  onChange: (change: Partial<Majority>) => void; onRemove: () => void;
}) {
  const full = m.threshold.num === m.threshold.den;
  return (
    <div class="majority">
      <div class="field">
        <label for={`mayoria-${id}`}>Nombre</label>
        <input id={`mayoria-${id}`} type="text" value={m.label} maxLength={120} onInput={(e) => onChange({ label: e.currentTarget.value })} />
      </div>
      <div class="field">
        <label for={`mayoria-${id}-fraccion`}>Se aprueba con</label>
        <div class="inline">
          <Comparison id={`mayoria-${id}-comparacion`} strict={m.strict} allowStrict={!full} onChange={(strict) => onChange({ strict })} />
          <RatioInput id={`mayoria-${id}-fraccion`} value={m.threshold} minNum={1} onChange={(threshold) => onChange({ threshold, strict: m.strict && threshold.num !== threshold.den })} />
          <select aria-label="Sobre qué se cuenta" value={m.base} onChange={(e) => onChange({ base: e.currentTarget.value as MajorityBase })}>
            {Object.entries(BASE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
      </div>
      {m.base === 'emitted' && (
        <label class="check" for={`mayoria-${id}-abst`}>
          <input id={`mayoria-${id}-abst`} type="checkbox" checked={m.abstentionsCount} onChange={(e) => onChange({ abstentionsCount: e.currentTarget.checked })} />
          Las abstenciones cuentan como voto emitido
        </label>
      )}
      <FieldIssues issues={issues} />
      <div class="row">
        <span class="hint" style={{ flex: '1 1 200px' }}>{usedBy.length ? `La usan: ${usedBy.join(', ')}.` : 'Ningún tipo de asunto la usa.'}</span>
        <button class="btn" disabled={usedBy.length > 0 || only} title={usedBy.length ? 'Reasigna esos tipos de asunto antes de quitarla' : undefined} onClick={onRemove}>Quitar mayoría</button>
      </div>
    </div>
  );
}

function Summary({ rules }: { rules: RuleSet }) {
  const s = summarize(rules);
  return (
    <div class="summary">
      <p>{s.quorum}</p>
      {s.matters.map((m) => <p key={m.id}><b>{m.label}:</b> {m.text}</p>)}
      <p>{s.tie}</p>
    </div>
  );
}

const STATUS_TEXT = { approved: 'Aprobada', rejected: 'Rechazada', repeat: 'Empate: se repite', 'needs-casting': 'Empate: falta el voto de calidad', 'no-quorum': 'Sin quórum al cerrar' } as const;

function PreviewPanel({ rules, members }: { rules: RuleSet; members: number }) {
  const [matterId, setMatterId] = useState(rules.matters[0]?.id ?? '');
  const [input, setInput] = useState<PreviewInput>(() => sample(members));
  useEffect(() => setInput(sample(members)), [members]);
  const current = rules.matters.some((m) => m.id === matterId) ? matterId : rules.matters[0]?.id ?? '';
  const p = preview(rules, current, input);
  const num = (key: keyof PreviewInput, label: string) => (
    <div class="field">
      <label for={`prueba-${key}`}>{label}</label>
      <input id={`prueba-${key}`} type="number" inputMode="numeric" min={0} value={input[key]}
        onInput={(e) => setInput({ ...input, [key]: Math.trunc(Number(e.currentTarget.value)) || 0 })} />
    </div>
  );
  return (
    <section class="panel">
      <h2>Prueba la regla</h2>
      <div class="field">
        <label for="prueba-asunto">Tipo de asunto</label>
        <select id="prueba-asunto" value={current} onChange={(e) => setMatterId(e.currentTarget.value)}>
          {rules.matters.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
      </div>
      <div class="preview-grid">
        {num('total', 'Integrantes')}{num('present', 'Presentes')}{num('for', 'A favor')}{num('against', 'En contra')}{num('abstain', 'Abstenciones')}
      </div>
      <div class="result" role="status" aria-live="polite">
        {p.kind === 'result' ? (
          <>
            <span class={`pill ${p.evaluation.approved ? 'ok' : p.evaluation.status === 'rejected' || p.evaluation.status === 'no-quorum' ? 'bad' : 'warn'}`}><i aria-hidden="true" />{STATUS_TEXT[p.evaluation.status]}</span>
            <p>{p.evaluation.reason}</p>
            <p class="meta">Quórum para abrir: {p.quorumNeed} · base {p.evaluation.base} · se necesitan {p.evaluation.need} a favor</p>
          </>
        ) : <p class={p.kind === 'no-quorum' ? '' : 'hint'}>{p.message}</p>}
      </div>
    </section>
  );
}

/** Datos de ejemplo: 80% presentes, casi todos votan. */
function sample(total: number): PreviewInput {
  const present = Math.round(total * 0.8);
  const forVotes = Math.round(present * 0.55);
  const against = Math.round(present * 0.3);
  return { total, present, for: forVotes, against, abstain: Math.max(0, present - forVotes - against - 1) };
}

function JsonEditor({ draft, onApply }: { draft: RuleSet; onApply: (r: RuleSet) => void }) {
  const [text, setText] = useState(() => JSON.stringify(draft, null, 2));
  const [issues, setIssues] = useState<Issue[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const apply = async () => {
    setError(null);
    setIssues([]);
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch (e) {
      setError(`El texto no es JSON válido: ${(e as Error).message}`);
      return;
    }
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) (parsed as { version?: unknown }).version = draft.version;
    setBusy(true);
    try {
      const r = await api<{ ok: boolean; errors: Issue[] }>('POST', '/api/rule-sets/validate', { ruleSet: parsed });
      if (r.ok) onApply(parsed as RuleSet);
      else setIssues(r.errors);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const importFile = async (file: File | undefined) => {
    if (!file) return;
    if (file.size > 200_000) { setError('El archivo es demasiado grande para ser un RuleSet.'); return; }
    setText(await file.text());
  };

  const exportFile = () => {
    const url = URL.createObjectURL(new Blob([JSON.stringify(draft, null, 2)], { type: 'application/json' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `reglas-${draft.version}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <section class="panel">
      <h2>RuleSet en JSON</h2>
      <p class="hint">Para quien prefiere editar el JSON o pasar reglas entre organizaciones. Los umbrales son fracciones <code>{'{ "num": 2, "den": 3 }'}</code>. La versión la pone el sistema. Ver docs/SPEC.md.</p>
      <ErrorBox error={error} />
      {issues.length > 0 && (
        <div class="error" role="alert">
          <p>El RuleSet tiene errores:</p>
          <ul>{issues.map((i) => <li key={i.path + i.message}><code>{i.path || '/'}</code> {i.message}</li>)}</ul>
        </div>
      )}
      <label for="json" class="sr-only">RuleSet en JSON</label>
      <textarea id="json" class="code" rows={24} spellcheck={false} value={text} onInput={(e) => setText(e.currentTarget.value)} />
      <div class="row">
        <button class="btn primary" disabled={busy} onClick={apply}>{busy ? 'Revisando…' : 'Aplicar al formulario'}</button>
        <button class="btn" onClick={exportFile}>Exportar .json</button>
        <label class="btn" for="json-archivo">Importar .json</label>
        <input id="json-archivo" class="sr-only" type="file" accept="application/json,.json" onChange={(e) => void importFile(e.currentTarget.files?.[0])} />
      </div>
    </section>
  );
}
